import { inject, provide, shallowRef, type InjectionKey, type ShallowRef } from "vue";

import type {
    CreatorPageProps,
    EditorPageProps,
    ListerPageProps,
    ViewerPageProps
} from "../types";

export const EditorContextKey: InjectionKey<EditorPageProps> =
    Symbol("EditorContext");
export const ListerContextKey: InjectionKey<ListerPageProps> =
    Symbol("ListerContext");
export const ListerItemsKey: InjectionKey<ShallowRef<any[]>> =
    Symbol("ListerItems");
export const CreatorContextKey: InjectionKey<CreatorPageProps> =
    Symbol("CreatorContext");
export const ViewerContextKey: InjectionKey<ViewerPageProps> =
    Symbol("ViewerContext");

/**
 * Provide Editor context to children
 */
export function provideEditorContext(props: EditorPageProps) {
    provide(EditorContextKey, props);
}

/**
 * Inject Editor context in children
 */
export function useEditorContext(): EditorPageProps {
    const context = inject(EditorContextKey);
    if (!context) {
        throw new Error(
            "useEditorContext must be used within an EditorProvider/Page"
        );
    }
    return context;
}

/**
 * Provide Lister context to children
 * @param props - Static props (excluding items for reactivity)
 * @param items - Optional ShallowRef for items (enables real-time updates)
 */
export function provideListerContext(props: ListerPageProps, items?: ShallowRef<any[]>) {
    provide(ListerContextKey, props);
    // Provide items as a separate ref for proper reactivity
    const itemsRef = items ?? shallowRef(props.items);
    provide(ListerItemsKey, itemsRef);
}

/**
 * Inject Lister context in children
 */
export function useListerContext(): ListerPageProps {
    const context = inject(ListerContextKey);
    if (!context) {
        throw new Error(
            "useListerContext must be used within a ListerProvider/Page"
        );
    }
    return context;
}

/**
 * Inject Lister items ref for reactive updates
 */
export function useListerItems(): ShallowRef<any[]> {
    const items = inject(ListerItemsKey);
    if (!items) {
        throw new Error(
            "useListerItems must be used within a ListerProvider/Page"
        );
    }
    return items;
}

/**
 * Provide Creator context to children
 */
export function provideCreatorContext(props: CreatorPageProps) {
    provide(CreatorContextKey, props);
}

/**
 * Inject Creator context in children
 */
export function useCreatorContext(): CreatorPageProps {
    const context = inject(CreatorContextKey);
    if (!context) {
        throw new Error(
            "useCreatorContext must be used within a CreatorProvider/Page"
        );
    }
    return context;
}

/**
 * Provide Viewer context to children
 */
export function provideViewerContext(props: ViewerPageProps) {
    provide(ViewerContextKey, props);
}

/**
 * Inject Viewer context in children
 */
export function useViewerContext(): ViewerPageProps {
    const context = inject(ViewerContextKey);
    if (!context) {
        throw new Error(
            "useViewerContext must be used within a ViewerProvider/Page"
        );
    }
    return context;
}
